import { CSSProperties, ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { api } from './api';
import { bookingStatus } from './tokens.js';
import type { Booking, BookingStatus, Money } from './types';

// The status chip: dot + word, always both; one colour per status,
// everywhere, read from the tokens. 26px pill per the drawn spec.
export function StatusChip({ status }: { status: BookingStatus }) {
  const t = (bookingStatus as Record<string, { fg: string; bg: string; border: string; dot: string; label: string }>)[
    status
  ] ?? bookingStatus.cancelled;
  return (
    <span className="chip" style={{ color: t.fg, background: t.bg, borderColor: t.border }}>
      <span className="chip-dot" style={{ background: t.dot }} />
      {t.label}
    </span>
  );
}

// ── The loading state, as a component (canon 04 build-order step 4) ───────
// "The four data states as real components — not conventions each screen
// re-implements." Empty and error were already components; loading was a
// per-screen <p>Loading…</p>. These three are its one voice: a Spinner for a
// button or an inline wait, a Skeleton for the shape of content about to land,
// and Loading — the panel/list default. All still under prefers-reduced-motion
// (the spin and shimmer are held in CSS).
export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <span className="spinner" role="status" aria-label={label} />;
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="skeleton" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className="skeleton-line" />
      ))}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <p className="loading" role="status">
      <span className="spinner" aria-hidden="true" /> {label}
    </p>
  );
}

// A rating as filled/empty stars — one renderer for every place a review is
// shown (the Reviews screen, the hotel Booking-detail once rated, the supplier
// Booking-detail), so the stars can never look different in two places. The
// numeric value stays available to screen readers; the fill rounds to a whole
// star for the glyphs.
export function Stars({ rating, of = 5 }: { rating: number; of?: number }) {
  const full = Math.round(rating);
  return (
    <span className="stars" aria-label={`${rating} out of ${of}`}>
      {Array.from({ length: of }, (_, i) => (
        <span key={i} className={`star${i < full ? ' on' : ''}`} aria-hidden="true">
          ★
        </span>
      ))}
    </span>
  );
}

// The web tab bar (canon 04 "Tab bar (web)") — a module's sub-views under a 2px
// saffron underline, drawn once so every tab strip reads identically. Each tab is
// a route (canon 09: the URL is the source of truth), so an alert or a deep link
// opens the exact sub-view. Absent on single-view modules, by not rendering it.
export function Tabs({ items }: { items: { label: ReactNode; href: string; active: boolean }[] }) {
  return (
    <nav className="tabstrip" role="tablist">
      {items.map((t) => (
        <a key={t.href} href={t.href} role="tab" aria-selected={t.active} className={t.active ? 'active' : ''}>
          {t.label}
        </a>
      ))}
    </nav>
  );
}

// The segmented control (canon 04 "Segmented control") — a filled-pill choice
// between mutually exclusive options, drawn once. Its active state is the
// sanctioned saffron-50 fill (a filled emphasis, never an outline or a left bar).
// Distinct from the tab bar: a segmented control flips a local mode in place, a
// tab strip navigates to another screen.
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  style,
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  style?: CSSProperties;
}) {
  return (
    <div className="segmented" role="tablist" style={style}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          className={value === o.value ? 'active' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Connection state (canon 04 "Connection state") — the dot-plus-word chip that
// says where a hotel↔supplier link stands, drawn once so the same state reads
// the same colour on the hotel directory and the supplier's My-hotels. The
// wording differs by side (a hotel "invited" them; to the supplier they "want to
// connect"), so the caller owns the label; the component owns the colour.
type ConnKind = 'connected' | 'pending' | 'dormant';
const CONN: Record<ConnKind, { fg: string; bg: string; border: string; dot: string }> = {
  // Connected — the healthy state, olive (success).
  connected: { fg: 'var(--color-success-dark)', bg: 'var(--color-success-light)', border: 'var(--color-success-border)', dot: 'var(--color-success)' },
  // Waiting on someone to accept — a calm neutral, not an alarm.
  pending: { fg: 'var(--text-muted)', bg: 'var(--bg-layer-4)', border: 'var(--border-medium)', dot: 'var(--border-strong)' },
  // Declined / burned / lapsed — quiet and out of the way.
  dormant: { fg: 'var(--text-light)', bg: 'var(--bg-layer-2)', border: 'var(--border-light)', dot: 'var(--border-medium)' },
};
export function ConnectionState({ kind, label }: { kind: ConnKind; label: string }) {
  const t = CONN[kind];
  return (
    <span className="chip" style={{ color: t.fg, background: t.bg, borderColor: t.border }}>
      <span className="chip-dot" style={{ background: t.dot }} />
      {label}
    </span>
  );
}

// Money as drawn: "€ 570.00" — space after the sign, mono, tabular, two
// decimals, currency always shown. Red is never used for a negative.
export function fmtMoney(money?: Money): string {
  if (!money) return '';
  const value = (money.minor / 100).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return money.currency === 'EUR' ? `€ ${value}` : `${money.currency} ${value}`;
}

export function MoneyText({ money }: { money?: Money }) {
  if (!money) return null;
  return <span className="money">{fmtMoney(money)}</span>;
}

// The "Statement · CSV" of canon S6: a client-side download, one row per named
// booking so every euro reconciles. Money is written in its decimal form with
// the currency in its own column — never a float in the API, and the ledger
// entry (the booking) is what each line points back to.
export function downloadCsv(filename: string, header: string[], rows: (string | number)[][]): void {
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Dates are always day-name plus date — never 20/02. Times in the
// property's wall clock: the ISO string carries the property offset, so we
// render its own wall time, not the device's.
export function formatWhen(iso: string): string {
  const wall = iso.slice(0, 16);
  const d = new Date(wall);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  return `${day} · ${wall.slice(11)}`;
}

export function formatDay(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

// "Waiting 45 min" / "Starts in 4 hours" — the queue chip's arithmetic.
export function ago(iso: string): string {
  const mins = Math.max(1, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (mins < 60) return `${mins} min`;
  const h = Math.round(mins / 60);
  return h < 24 ? `${h} h` : `${Math.round(h / 24)} d`;
}

export function untilStart(iso: string): number {
  return Date.parse(iso) - Date.now();
}

// Money multiplication with the integer guard — minor units never leave the
// integers. The one platform-side money arithmetic, beside fmtMoney above.
export function mulMoney(m: Money, by: number): Money {
  if (!Number.isInteger(m.minor) || !Number.isInteger(by)) {
    throw new Error('money stays in integer minor units');
  }
  return { minor: m.minor * by, currency: m.currency };
}

// Property-offset arithmetic, once. The ISO strings carry the property's own
// offset; composing a new wall-clock time in that offset happens here and
// nowhere else.
export function offsetMs(offset: string): number {
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(offset);
  if (!m) return 0;
  return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) * 60_000;
}

export function toLocalIso(d: Date, offset: string): string {
  return new Date(d.getTime() + offsetMs(offset)).toISOString().slice(0, 19) + offset;
}

// A stored UTC stamp (timeline entries, invitation dates), rendered in the
// property's local time with a day name — never raw UTC, never 20/02
// (canon 10's time rules).
export function formatStamp(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

// The property's date (YYYY-MM-DD) at a given moment — "today" in the
// property's day, never the device's, never UTC.
export function zoneDate(timezone: string, atMs: number): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date(atMs));
}

// The property's offset at a given moment, derived from its IANA zone
// (canon 15: never a stored offset — a Greek season crosses a DST boundary
// and a stored +03:00 is wrong for half of it).
export function zoneOffset(timezone: string, atMs: number): string {
  const name =
    new Intl.DateTimeFormat('en-GB', { timeZone: timezone, timeZoneName: 'longOffset' })
      .formatToParts(new Date(atMs))
      .find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const m = /^GMT([+-]\d{2}:\d{2})$/.exec(name);
  return m ? m[1]! : '+00:00';
}

// The queue card, drawn once (canon: Queue template — the primary action is
// on the card). Three screens compose it; none re-implements it.
export function QCard({
  chip,
  title,
  meta,
  amount,
  caption,
  when,
  open,
  escalated,
  style,
  onClick,
}: {
  chip: ReactNode;
  title: ReactNode;
  meta: ReactNode;
  amount?: string;
  caption?: string;
  when: ReactNode;
  open: string;
  escalated?: boolean;
  style?: CSSProperties;
  onClick: () => void;
}) {
  return (
    // A full keyboard path is a canon rule, not an enhancement — the card
    // is a button in all but tag.
    <div
      className={`qcard ${escalated ? 'escalated' : ''}`}
      style={style}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="top">
        <div className="left">
          {chip}
          <div className="product">{title}</div>
          <div className="meta">{meta}</div>
        </div>
        {amount !== undefined && (
          <div className="right">
            <div className="amount">{amount}</div>
            {caption && <div className="caption">{caption}</div>}
          </div>
        )}
      </div>
      <div className="foot">
        <span className="when">{when}</span>
        <span className="open">{open}</span>
      </div>
    </div>
  );
}

// ── The panel, holding canon 09's composition rules by construction ───────
// A panel is a head and a body; panel-level actions live in the head's meta
// slot; `flush` lets a table sit edge to edge. Panels do not nest — rule 6
// is thrown, not reviewed.

const PanelDepth = createContext(0);

export function Panel({
  title,
  meta,
  flush,
  onClick,
  style,
  children,
}: {
  title?: string;
  meta?: ReactNode;
  flush?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const depth = useContext(PanelDepth);
  if (depth > 0) {
    throw new Error('Panels do not nest (canon 09, rule 6). Use a divider and a subhead.');
  }
  return (
    <PanelDepth.Provider value={depth + 1}>
      <section className="card" style={style} onClick={onClick}>
        {(title || meta) && (
          <div className="card-head">
            {title && <h2>{title}</h2>}
            {meta && <span className="meta">{meta}</span>}
          </div>
        )}
        {flush ? children : <div className="card-body">{children}</div>}
      </section>
    </PanelDepth.Provider>
  );
}

// The page head: one title, one subtitle, page-level actions on the right —
// rule 3's "page-level actions live in the page head" as a component.
export function PageHead({ title, sub, children }: { title: string; sub?: string; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {sub && <p className="sub">{sub}</p>}
      </div>
      {children && <div className="actions">{children}</div>}
    </div>
  );
}

// The ink hero: one per screen, owning its most important number.
export function InkHero({
  eyebrow,
  figure,
  unit,
  explain,
}: {
  eyebrow: string;
  figure: ReactNode;
  unit?: string;
  explain?: ReactNode;
}) {
  return (
    <div className="hero" style={{ marginBottom: 20 }}>
      <div className="eyebrow">{eyebrow}</div>
      <div className="q-hero-count">
        <span className="figure">{figure}</span>
        {unit && <span className="unit">{unit}</span>}
      </div>
      <p className="explain">{explain ?? ' '}</p>
    </div>
  );
}

// ── The undo toast: two windows, one component, built once (canon 10) ─────
// The window arrives from the API on the booking itself — 30 s for a
// person, 30 min for Eva — so the toast never invents a duration.

interface ToastState {
  bookingId: string;
  title: string;
  sub: string;
  until: number;
}

// A toast can survive exactly one navigation — H1's create toast is shown
// on the screen the hotel lands on, not the form they left. Same component,
// one handoff door.
const CARRY_KEY = 'iso.carriedToast';

export function carryToast(booking: Booking, title: string, sub: string): void {
  const until = booking.undo ? Date.parse(booking.undo.until) : 0;
  if (until > Date.now()) {
    sessionStorage.setItem(CARRY_KEY, JSON.stringify({ bookingId: booking.id, title, sub, until }));
  }
}

function consumeCarried(): ToastState | null {
  try {
    const raw = sessionStorage.getItem(CARRY_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(CARRY_KEY);
    const t = JSON.parse(raw) as ToastState;
    return t.until > Date.now() ? t : null;
  } catch {
    return null;
  }
}

export function useUndoToast(onChanged: () => void) {
  const [toast, setToast] = useState<ToastState | null>(consumeCarried);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), toast.until - Date.now());
    return () => window.clearTimeout(t);
  }, [toast]);

  const show = (booking: Booking, title: string, sub: string) => {
    const until = booking.undo ? Date.parse(booking.undo.until) : 0;
    if (until > Date.now()) setToast({ bookingId: booking.id, title, sub, until });
  };

  const undo = async () => {
    if (!toast) return;
    const id = toast.bookingId;
    setToast(null);
    try {
      await api('POST', `/bookings/${id}/undo`);
    } catch {
      // The window closed under us — the reload below shows the truth.
    }
    onChanged();
  };

  const element = toast ? (
    <div className="toast" role="status">
      <span>
        <span className="t">{toast.title}</span>
        <br />
        <span className="s">{toast.sub}</span>
      </span>
      <button className="undo-btn" onClick={undo}>
        Undo
      </button>
      <span className="track" />
      <span className="bar" style={{ animationDuration: `${Math.max(0, toast.until - Date.now())}ms` }} />
    </div>
  ) : null;

  return { show, element };
}

// ── The booking timeline (canon 04 "Booking timeline"), drawn once ────────
// The same actor-stamped lifecycle renders in the hotel Booking-detail drawer
// and the supplier Booking Record: each transition's status chip with its
// reason/note, and — right — the property-local stamp (formatStamp keeps it
// day-name-first, never raw UTC, canon 10) with the actor beneath. Both screens
// composed this identically; now they call it.
export function BookingTimeline({ timeline, tz }: { timeline?: Booking['timeline']; tz: string }) {
  return (
    <>
      <h2 style={{ fontSize: 'var(--text-lg)', color: 'var(--text-secondary)', margin: '4px 0 8px' }}>Timeline</h2>
      <div className="kv">
        {(timeline ?? []).map((t, i) => (
          <div className="kvrow" key={i}>
            <span>
              <StatusChip status={t.to} />
              {(t.reason || t.note) && (
                <span className="hint" style={{ display: 'block', marginTop: 4 }}>
                  {t.reason}
                  {t.reason && t.note ? ' — ' : ''}
                  {t.note}
                </span>
              )}
            </span>
            <span style={{ textAlign: 'right' }}>
              <span className="v" style={{ fontSize: 'var(--text-xs)' }}>{formatStamp(t.at, tz)}</span>
              <br />
              <span className="hint mono" style={{ fontSize: 'var(--text-2xs)' }}>{t.actor}</span>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

// ── The month calendar (canon 04 "Calendar" · Register / Full grid) ───────
// Drawn once. The hotel's bookings-across-suppliers and the supplier's own
// month render through the same grid: six Monday-first weeks, each booking a
// chip coloured by lifecycle. The caller owns the fetch (its own partition,
// token-pinned — DECISION-020) and where a chip goes; the supplier also passes
// its blocked days (a chip that links to Availability). The component owns the
// grid and the month controls.
const CAL_DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const calYmd = (d: Date) => d.toISOString().slice(0, 10);

export function addMonth(m: string, delta: number): string {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(Date.UTC(y!, mo! - 1 + delta, 1, 12));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function monthLabel(m: string): string {
  return new Date(`${m}-15T12:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function dotColour(status: string): string {
  const t = (bookingStatus as Record<string, { dot: string }>)[status];
  return t?.dot ?? 'var(--border-strong)';
}

// The six-week (Monday-first) window for a month, and its from/to — so the
// screen fetches exactly what the grid will show. One derivation, shared.
export function monthWindow(m: string): { cells: string[]; from: string; to: string } {
  const first = new Date(`${m}-01T12:00:00Z`);
  const lead = (first.getUTCDay() + 6) % 7; // Monday = 0
  const gridStart = new Date(first.getTime() - lead * 86_400_000);
  const cells = Array.from({ length: 42 }, (_, i) => calYmd(new Date(gridStart.getTime() + i * 86_400_000)));
  return { cells, from: cells[0]!, to: cells[41]! };
}

export function MonthCalendar({
  month,
  thisMonth,
  todayStr,
  bookings,
  blocked,
  onMonth,
  onOpenBooking,
  onOpenBlocked,
  chipTitle,
}: {
  month: string;
  thisMonth: string;
  todayStr: string;
  bookings: Booking[] | null;
  blocked?: Set<string>;
  onMonth: (next: string) => void;
  onOpenBooking: (b: Booking) => void;
  onOpenBlocked?: () => void;
  chipTitle?: (b: Booking) => string;
}) {
  const { cells } = monthWindow(month);
  const byDate = new Map<string, Booking[]>();
  for (const b of bookings ?? []) {
    const d = b.start.slice(0, 10);
    if (!byDate.has(d)) byDate.set(d, []);
    byDate.get(d)!.push(b);
  }
  for (const list of byDate.values()) list.sort((a, b) => a.start.localeCompare(b.start));

  return (
    <>
      <div className="cal-head">
        <button className="btn ghost" aria-label="Previous month" onClick={() => onMonth(addMonth(month, -1))}>←</button>
        <span className="cal-month">{monthLabel(month)}</span>
        <button className="btn ghost" aria-label="Next month" onClick={() => onMonth(addMonth(month, 1))}>→</button>
        {month !== thisMonth && (
          <button className="btn ghost" onClick={() => onMonth(thisMonth)}>Today</button>
        )}
      </div>
      <Panel flush style={{ overflow: 'hidden' }}>
        <div className="cal-grid">
          {CAL_DOW.map((d) => (
            <div key={d} className="cal-dow">{d}</div>
          ))}
          {cells.map((date) => {
            const dayBookings = byDate.get(date) ?? [];
            const inMonth = date.startsWith(month);
            const isBlocked = blocked?.has(date) ?? false;
            return (
              <div key={date} className={`cal-cell ${inMonth ? '' : 'other'} ${date === todayStr ? 'today' : ''} ${isBlocked ? 'blocked' : ''}`}>
                <span className="cal-date">{Number(date.slice(8, 10))}</span>
                {isBlocked && onOpenBlocked && (
                  <button className="cal-chip block" onClick={onOpenBlocked} title="You blocked this day">
                    <span className="cal-name">Blocked</span>
                  </button>
                )}
                {dayBookings.map((b) => (
                  <button
                    key={b.id}
                    className="cal-chip"
                    onClick={() => onOpenBooking(b)}
                    title={chipTitle ? chipTitle(b) : (b.productName ?? b.productId)}
                  >
                    <span className="cal-dot" style={{ background: dotColour(b.status) }} />
                    <span className="cal-when">{b.start.slice(11, 16)}</span>
                    <span className="cal-name">{b.productName ?? b.productId}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </Panel>
    </>
  );
}
